import React, {useState, useEffect} from "react"
import {getCondoById, useGetCondoPriceAnalysisQuery} from "@/api/condo"
import {useNavigate} from "react-router-dom"
import ImagesColumn from "@/components/carrousel/ImagesColumn"
import dayjs from "dayjs"
import EditRoundedIcon from "@mui/icons-material/EditRounded"
import CameraAltIcon from "@mui/icons-material/CameraAlt"
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile"
import {YouTubeEmbed, TikTokEmbed} from "react-social-media-embed"
import EditButton from "../properties/EditButton"
import PageSection from "@/layouts/PageSection"
import {CONDO_SECTIONS} from "@/constants"
import FileSection from "@/components/files/FilesSection"
import CondoPropertiesTable from "@/components/condos/CondoPropertiesTable"
import {useAuthContext} from "@/hooks/useAuthContext"
import {AcapulcoCondoDoc, CondoDoc, RegionCode, regionTextByCode} from "@casas-diamante/types"
import {getImageURL, displayZoneLabel} from "@/utils"
import {useLazyLoadOnView} from "@/hooks/useCondoLazyLoad"
import CondoPriceAnalysisTable from "@/components/condos/CondoPriceAnalysisTable"

interface Props {
   condId: string
   condoData?: CondoDoc | AcapulcoCondoDoc
   lazy?: boolean
   lazyRootMargin?: string
   shouldNavigate?: boolean
   styles: {
      disponibilidadBtn: string
   }
}

type CondoInfoData = (CondoDoc | AcapulcoCondoDoc) & {
   access_to_beach?: string
   antiquity?: string
   beach_club?: boolean
   createdAt?: {
      _seconds: number
   }
   pet_friendly?: boolean
   vacation_rentals_allowed?: boolean
   videos?: string[]
   videoId?: string
}

function CondominioInfo({
   condId,
   condoData,
   lazy = false,
   lazyRootMargin = "600px 0px",
   shouldNavigate = false,
}: Props) {
   const navigate = useNavigate()
   const [condo, setCondo] = useState<CondoInfoData | null>(null)
   const returnTo = `/condominios/${condId}`
   const condoSection = useLazyLoadOnView<HTMLDivElement>({
      active: lazy,
      loadImmediately: !lazy,
      rootMargin: lazyRootMargin,
      resetKey: `${condId}:${lazy}`,
   })
   const {data: priceAnalysis, isLoading: loadingPriceAnalysis, isError: priceAnalysisError} =
      useGetCondoPriceAnalysisQuery(condId, {skip: !condoSection.shouldLoad})
   const {user} = useAuthContext()
   const condoVideos = Array.isArray(condo?.videos)
      ? condo?.videos ?? []
      : condo?.videoId
      ? [`https://www.youtube.com/watch?v=${condo.videoId}`]
      : []

   useEffect(() => {
      setCondo(null)
   }, [condId])

   useEffect(() => {
      if (!condoSection.shouldLoad) return

      let isMounted = true
      const fetchCondo = async () => {
         const condo = await getCondoById(condId)
         if (!isMounted) return
         setCondo(condo)
      }
      if (!condoData) {
         fetchCondo()
      } else {
         setCondo(condoData)
      }

      return () => {
         isMounted = false
      }
   }, [condId, condoData, condoSection.shouldLoad])

   return (
      <>
         {!condoSection.shouldLoad && (
            <div
               id="condo-info"
               ref={condoSection.ref}
               className="min-h-[1px]"
               aria-hidden="true"
            />
         )}
         {condo && (
            <div className="mb-10 flex flex-col" id="condo-info">
               {user.admin && (
                  <section className="flex gap-4 items-center flex-col sm:flex-row sm:self-end">
                     <EditButton
                        label="Editar Condominio"
                        onClick={() =>
                           navigate(`/condominios/editCondominio/${condId}`, {
                              state: {condominio: condo},
                           })
                        }
                        icon={<EditRoundedIcon fontSize="small" />}
                     />
                     <EditButton
                        label="Editar Fotos"
                        onClick={() =>
                           navigate(`/condominios/editPhotos/${condId}`, {
                              state: {condominio: condo},
                           })
                        }
                        icon={<CameraAltIcon fontSize="small" />}
                     />
                     <EditButton
                        label="Editar archivos"
                        onClick={() =>
                           navigate(`/condominios/editFiles/${condId}`, {
                              state: {condominio: condo},
                           })
                        }
                        icon={<InsertDriveFileIcon fontSize="small" />}
                     />
                  </section>
               )}
               {condo.createdAt && (
                  <p className="my-5 text-center">
                     Creado el: {dayjs.unix(condo.createdAt?._seconds).format("DD-MM-YYYY")}
                  </p>
               )}
               <section className="grid grid-cols-2 md:flex md:flex-wrap gap-4 items-center justify-center mt-4">
                  {CONDO_SECTIONS.map((section, i) => (
                     <button
                        key={i}
                        className="bg-blue rounded text-white px-4 py-1"
                        onClick={() => {
                           const element = document.getElementById(section.id)
                           element?.scrollIntoView({behavior: "smooth"})
                        }}
                     >
                        {section.title}
                     </button>
                  ))}
               </section>
               <header className="flex flex-col md:flex-row items-center justify-center mb-8 md:min-h-[400px] py-2 md:py-8">
                  <img
                     src={getImageURL(condo.mainImage)}
                     alt="condominio"
                     className="md:w-[50%] h-auto w-full object-cover rounded-lg mx-auto my-5"
                  />
                  <div className="md:w-[50%] w-full md:ml-4 flex flex-col gap-2">
                     <h2 className="font-bold text-blue">Condominio</h2>
                     <h1
                        className={
                           shouldNavigate ? "font-bold underline cursor-pointer" : "font-bold"
                        }
                        onClick={() => (shouldNavigate ? navigate("/condominios/" + condId) : null)}
                     >
                        {condo.name}
                     </h1>
                     <h2 className="text-green">{regionTextByCode[condo.region as RegionCode]}</h2>
                     <p className="font-bold text-blue text-sm">
                        {condo.class ? "Clasificación: " + condo.class : "Sin clasificar"}
                     </p>
                     {condo.amenities && (
                        <>
                           <h4>Amenidades</h4>
                           <div className="flex flex-wrap text-sm gap-2">
                              {condo.amenities?.map((amenidad: string) => (
                                 <div
                                    key={amenidad}
                                    className="border rounded border-blue text-blue p-1"
                                 >
                                    {amenidad}
                                 </div>
                              ))}
                           </div>
                        </>
                     )}
                     <div>
                        <h4>Zona</h4>
                        <p>{displayZoneLabel(condo.zone)}</p>
                     </div>
                     <div>
                        <h4>Ubicación</h4>
                        {condo.mapsShareUrl && (
                           <a
                              href={condo.mapsShareUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="underline text-blue"
                           >
                              {condo.mapsShareUrl}
                           </a>
                        )}
                        <p>Dirección: {condo.address}</p>
                     </div>
                  </div>
               </header>
               <CondoPriceAnalysisTable
                  analysis={priceAnalysis}
                  loading={loadingPriceAnalysis}
                  error={priceAnalysisError}
               />
               <PageSection
                  id="condo-description"
                  title="Información"
                  className="flex flex-col gap-2"
               >
                  <div>
                     <h3 className="text-blue">Descripción del condominio</h3>
                     <p className="whitespace-pre-line">{condo.desc}</p>
                  </div>
                  <div>
                     <h3>Más información</h3>
                     <p>Acceso a la playa: {condo.access_to_beach}</p>
                     <p>Antigüedad: {condo.antiquity}</p>
                     <p>Club de playa: {condo.beach_club ? "Sí" : "No"}</p>
                     <p>Pet friendly: {condo.pet_friendly ? "Sí" : "No"}</p>
                     <p>
                        Rentas vacacionales permitidas:
                        {condo.vacation_rentals_allowed ? "Sí" : "No"}
                     </p>
                  </div>
               </PageSection>
               <PageSection id="condo-files" title="Archivos">
                  <FileSection id={condo.id} />
               </PageSection>
               <PageSection
                  id="condo-properties"
                  title={`Inmuebles en ${condo.name}`}
                  className="p-0"
               >
                  <CondoPropertiesTable key={condId} condoId={condId} returnTo={returnTo} />
               </PageSection>
               <PageSection id="condo-images" title="Imágenes">
                  {condo.images && condo.orderedSections && (
                     <ImagesColumn images={condo.images} orderedSections={condo.orderedSections} />
                  )}
               </PageSection>
               <PageSection id="condo-videos" title="Videos">
                  {condoVideos.map((url: string, index: number) => {
                     if (url.includes("youtube")) {
                        return (
                           <div key={index} className="my-6">
                              <div className="md:hidden">
                                 <YouTubeEmbed url={url} width={300} height={200} />
                              </div>
                              <div className="md:flex hidden">
                                 <YouTubeEmbed url={url} width={700} height={400} />
                              </div>
                           </div>
                        )
                     }
                     if (url.includes("tiktok")) {
                        return (
                           <div key={index} className="flex my-6">
                              <TikTokEmbed url={url} />
                           </div>
                        )
                     }
                     return null
                  })}
               </PageSection>
               <PageSection
                  id="condo-location"
                  title="Ubicación"
                  className="flex flex-col items-center text-center mx-4 md:mx-[20%] max-w-full"
               >
                  {condo.googleMaps && (
                     <iframe
                        src={condo.googleMaps}
                        className="my-6 h-[200px] w-[300px] md:h-[450px] md:w-[700px] mx-auto"
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                     />
                  )}
                  {condo.mapsShareUrl && (
                     <>
                        <p className="font-bold">Ver Ubicación</p>
                        <a
                           href={condo.mapsShareUrl}
                           target="_blank"
                           rel="noreferrer"
                           className="text-blue underline truncate"
                        >
                           {condo.mapsShareUrl}
                        </a>
                     </>
                  )}
                  {condo.address && (
                     <p className="my-4 text-sm md:mx-[20%]">Dirección: {condo.address}</p>
                  )}
               </PageSection>
            </div>
         )}
      </>
   )
}

export default CondominioInfo
